import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Keep original protocol (http/https) from the environment variable
const formattedUrl = supabaseUrl.trim().replace(/\/$/, '');

// Enhanced error handling for client creation
let supabase;
try {
  supabase = createClient(formattedUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'x-application-name': 'AgentIA',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    },
    db: {
      schema: 'public'
    }
  });
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  throw new Error('Error al inicializar la conexión con Supabase. Por favor, recarga la página.');
}

// Add error event listener
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    // Clear any cached data
    localStorage.removeItem('supabase.auth.token');
    supabase.auth.refreshSession();
  }
});

// Enhanced error handling for failed requests
supabase.handleFailedRequest = async (error: any) => {
  console.error('Supabase request failed:', error);
  
  // Check if error is due to network connectivity
  if (!navigator.onLine) {
    throw new Error('No hay conexión a internet. Por favor, verifica tu conexión y vuelve a intentarlo.');
  }

  // Check if error is due to CORS
  if (error.message?.includes('Failed to fetch')) {
    console.error('Possible CORS issue:', {
      url: formattedUrl,
      error: error.message
    });
    throw new Error('Error de conexión con el servidor. Por favor, intenta nuevamente en unos momentos.');
  }

  // Check if error is due to authentication
  if (error.status === 401) {
    await supabase.auth.refreshSession();
    throw new Error('Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.');
  }

  // Handle rate limiting
  if (error.status === 429) {
    throw new Error('Has excedido el límite de solicitudes. Por favor, espera un momento y vuelve a intentarlo.');
  }

  // Handle service unavailable
  if (error.status === 503) {
    throw new Error('El servicio no está disponible en este momento. Por favor, intenta más tarde.');
  }

  // Handle other errors
  throw new Error('Error al conectar con el servidor. Por favor, intenta nuevamente.');
};

// Add request interceptor with enhanced error handling
const originalFetch = window.fetch;
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  // Network connectivity check
  if (!navigator.onLine) {
    throw new Error('No hay conexión a internet. Por favor, verifica tu conexión y vuelve a intentarlo.');
  }
  
  try {
    const response = await originalFetch(input, init);
    
    // Check if the response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error: any) {
    console.error('Fetch error:', error);
    
    if (error.message?.includes('Failed to fetch')) {
      throw new Error('Error de conexión con el servidor. Por favor, verifica tu conexión a internet.');
    }
    
    throw error;
  }
};

export default supabase;

export { supabase }